/**
 * PlayChess V2 — the screen root.
 *
 * It does FIVE things and nothing else (UX spec §6a "gameplay island"):
 *  - registers the socket handlers, the effects and the unmount cleanups;
 *  - reads everything that lives in a React CONTEXT exactly once (socket
 *    connectivity, navigation, reconnect, the toast) and hands it to a store or
 *    a module ref, so nothing under it needs a context; `rail/navigateRef.ts`
 *    also keeps the screen's own route NAME next to the nav object (N1, fix N
 *    2026-10-06), for the same reason;
 *  - mirrors `session.connected`, `session.anotherTabActive` (fix I: EITHER
 *    `props.changeClient` — the server's real `change_client_connection`,
 *    fix E6's missing half — OR GamePlayStore's `showChangeInClient`, a
 *    different path — so the shared `selectBoardProps` can lock the board
 *    without reading either source itself) and the socket's auth flag
 *    (`Strip/authStatus`, a PROP) — fix E6 / fix I. The same effect refreshes
 *    `Strip/authStatus`'s `clientChanged` and `clearRemoteClientChanged`
 *    (`props.setChangeClient(false)`), which is how `overlay/ClientChanged`
 *    — the only place either signal surfaces now — stays context-free and
 *    prop-free too;
 *  - adopts `route.params.tournamentId` / `isJoined` into the store (M4): only
 *    the ROOT sees route params, so it is what calls
 *    `actions.setTournamentContext` — the tournament FAMILY (Swiss vs arena) is
 *    not on the params and is filled in later by the rail panels' details
 *    fetch (`rail/tournament/panels/{DetailsPanel,StandingsPanel}/model.ts`,
 *    fix G — `pages/tournament` is gone); the same route is also what opens
 *    the home page's Tournaments tab when it carries NO tournament id at all
 *    (N5, fix N) — a bare landing on `CCTournaments` (the sidebar, a deep
 *    link, a notification), never a row opened from wherever the player
 *    already was (N4 keeps `tournamentId` on the params for that hop);
 *  - raises `chromeHidden` while a board is on screen (D8 revised 2026-10-06,
 *    `chrome/useChromeHidden`), on both viewports;
 *  - renders `<chrome/PlayHeader />` above `<PlayChessV2Body />` — PHONE ONLY
 *    (owner decision): the desktop's permanent drawer sidebar is already an
 *    exit control, so nothing replaces its hidden header there. A THIRD
 *    sibling, `<overlay/ClientChanged />` (fix I), renders unconditionally
 *    after the body so its scrim covers the lobby AND the game; none of the
 *    three components takes props.
 *
 * It deliberately does NOT measure anything: the layout that matters is the
 * CONTENT BOX (the app header and the drawer are outside this screen), which
 * only `Body` can see — it measures itself with `onLayout` and writes
 * `viewport` / `railWidth` / `boardSize` into `useV2Ui`.
 *
 * Because the body takes no props, a SocketProvider state change (inbox,
 * notifications, changeClient) re-renders THIS component and stops there.
 */
import React, {useEffect, useRef} from 'react';

import {useSocketReconnect} from '@/Hooks/UseSocket';
import useSocketConnected from '@/Hooks/UseSocket/useSocketConnected';
import useGamePlayState from '@/Store/GamePlayStore';
import {
  bridgeSetJoinedTournament,
  bridgeSetJoinedTournamentId,
} from '@/Store/liveGame/gamePlayStoreWrites';
import {liveGameStore} from '@/Store/liveGameStore';

import PlayChessV2Body from './Body';
import {useSocketAuth} from './Strip/authStatus';
import PlayHeader from './chrome/PlayHeader';
import useChromeHidden from './chrome/useChromeHidden';
import useAssistEffects from './hooks/useAssistEffects';
import useAutoJoin from './hooks/useAutoJoin';
import useGameplayEffects from './hooks/useGameplayEffects';
import useGameplayHandlers from './hooks/useGameplayHandlers';
import useTournamentEffects from './hooks/useTournamentEffects';
import useUnmountCleanup from './hooks/useUnmountCleanup';
import ClientChanged from './overlay/ClientChanged';
import {useLobby} from './pages/home/lobbyStore';
import {setNavigation, setReconnect} from './rail/navigateRef';
import useByeProbe from './rail/tournament/useByeProbe';
import useSounds from './rail/useSounds';
import {useV2Ui} from './theme/useV2Ui';

/** fix I: `props.setChangeClient` is undefined for any caller that omits it
 * (the design lab, a fresh test render) — this is what `clearRemoteClientChanged`
 * falls back to until there is a prop to wrap. */
const noop = () => {};

/**
 * The route NAME as a literal, like `chrome/useBackAction`'s `DASHBOARD_ROUTE`:
 * `@/navigators/StackConstants` imports every screen (including this one), so
 * importing it from inside the V2 tree makes a webpack module cycle (eager-
 * graph landmine, ARCHITECTURE §13).
 */
const TOURNAMENTS_ROUTE = 'CCTournaments';

function PlayChessV2(props: any) {
  useGameplayHandlers(props);
  useGameplayEffects();
  // M4: the seven tournament / arena / watch effects a reducer cannot own
  useTournamentEffects();
  // fix AA: the reload-safe backstop for a bye — `bye_response` is the live
  // path, this is what proves one from the round's pairings after a reload.
  useByeProbe();
  // the dashboard's "Play" intent, consumed once (flow 'autoJoin')
  useAutoJoin();
  // fix F-D / D2+D3: the assisted game's per-move POST and its live eval bar
  useAssistEffects();
  useUnmountCleanup();
  useSounds();
  // D8 revised 2026-10-06: hide the shell's header only while a BOARD is on
  // screen (not the lobby / seeking / tournament PAGE), on BOTH viewports (it
  // frees board height on desktop too). The exit control that takes away is
  // `chrome/PlayHeader` — PHONE ONLY by owner decision: the desktop's
  // permanent drawer sidebar already is one, so no Back control (or its
  // leave-game confirm) renders there.
  useChromeHidden();
  const phone = useV2Ui(s => s.viewport === 'phone');

  const connected = useSocketConnected();
  const reconnect = useSocketReconnect();

  /**
   * M4 — the tournament CONTEXT, from the route.
   *
   * `route.params.tournamentId` is what both screens read (old index.js
   * L451-470) and `resolveFlow` has already turned it into flow 'tournament'.
   * A Puzzle Royale deep link's params are NOT ours (old L456-463, rule 0 of
   * `resolveFlow`), so `royaleTournamentId` wins. `mode` is deliberately left
   * null: only the DETAILS response says whether it is a Swiss or an arena, and
   * the rail's Details/Standings panel models (`rail/tournament/panels/*`) call
   * `setTournamentContext` again with it (fix G).
   * `setTournamentContext` is idempotent for an unchanged id, so a re-render
   * cannot reset the slice.
   */
  const royaleTournamentId = props?.route?.params?.royaleTournamentId;
  const tournamentId = royaleTournamentId
    ? undefined
    : props?.route?.params?.tournamentId;
  useEffect(() => {
    if (tournamentId) {
      liveGameStore
        .getState()
        .actions.setTournamentContext({id: String(tournamentId)});
    }
  }, [tournamentId]);

  /**
   * N5 (owner, 2026-10-06): a BARE landing on the `CCTournaments` route — the
   * sidebar link, a deep link, a notification, never a row opened from
   * wherever the player already was (N4's own hop keeps `tournamentId` on the
   * params) — opens the home page on its Tournaments tab. The Play route
   * never touches it, so the viewer's own persisted tab choice
   * (`pages/home/lobbyStore.ts`) stands.
   */
  useEffect(() => {
    if (props?.route?.name === TOURNAMENTS_ROUTE && !tournamentId) {
      useLobby.getState().setTab('tournaments');
    }
  }, [props?.route?.name, tournamentId]);

  /**
   * …and the deep link that joins on arrival: `route.params.isJoined` (the old
   * screen's own arrival join, which also swallows the rejection). Once per
   * mount — the join promise's answer is on `tournament.join`, which the page
   * reads reactively.
   */
  const joinOnEntry = royaleTournamentId
    ? undefined
    : props?.route?.params?.isJoined;
  const joinedOnEntry = useRef(false);
  useEffect(() => {
    if (
      (joinOnEntry === true || joinOnEntry === 'true') &&
      tournamentId &&
      !joinedOnEntry.current
    ) {
      joinedOnEntry.current = true;
      liveGameStore
        .getState()
        .actions.joinTournament(String(tournamentId))
        .catch(() => {
          // the answer is data on `tournament.join`; nothing to do here
        });
    }
  }, [joinOnEntry, tournamentId]);

  // Mirror the socket's connectivity into the store so `Strip` (and anything
  // else below) never has to touch the socket context.
  useEffect(() => {
    liveGameStore.getState().setSession({connected});
  }, [connected]);

  /**
   * Fix I: "opened in another tab" has TWO sources now — the server's own
   * `change_client_connection` (`SocketProvider` → `props.changeClient`, fix
   * E6's missing half) and GamePlayStore's `showChangeInClient` (a DIFFERENT
   * path, written by `Components/ChallengeComponent`). Either means this tab
   * is stranded, so the shared `selectBoardProps` (Store/liveGame/
   * selectors.ts) locks the board off the OR of the two without reading
   * either source itself. `Strip` no longer has a face for this (fix I3):
   * `overlay/ClientChanged` is the only place it surfaces now, and it reads
   * the mirror below (`Strip/authStatus`'s `clientChanged`) rather than
   * GamePlayStore or the prop directly, so it stays context-free too.
   * `clearRemoteClientChanged` is the card's only way back INTO the prop
   * (`props.setChangeClient(false)`) — refreshed here, alongside the flag,
   * because the ROOT is the only component allowed to read a prop.
   */
  const anotherTabFromStore = useGamePlayState(s => !!s.showChangeInClient);
  const anotherTabActive = Boolean(props?.changeClient) || anotherTabFromStore;
  const setChangeClientProp = props?.setChangeClient;
  useEffect(() => {
    liveGameStore.getState().setSession({anotherTabActive});
    useSocketAuth.getState().setClientChanged(anotherTabActive);
    useSocketAuth
      .getState()
      .setClearRemoteClientChanged(
        setChangeClientProp ? () => setChangeClientProp(false) : noop,
      );
  }, [anotherTabActive, setChangeClientProp]);

  /**
   * Fix E6: and the AUTH flag, which is a PROP (`Application.tsx` owns it and
   * hands the same value to `SocketProvider`). Only the root may read a prop,
   * so it pushes it into the web-only `Strip/authStatus` store; `undefined`
   * (the design lab, any caller that does not pass it) counts as fine.
   */
  const authComplete = props?.authComplete !== false;
  useEffect(() => {
    useSocketAuth.getState().setComplete(authComplete);
  }, [authComplete]);

  /**
   * Fix E9, the fourth transition of the `joinedTournament` pair: the old screen
   * clears both in its profile effect, i.e. on every MOUNT (index.js L532-533),
   * and the other three (join / withdraw / tournament end) are socket
   * transitions and live in the shared reducer. This one stays platform-side
   * because it is a SCREEN-LIFECYCLE write, and because the app's old screen has
   * no equivalent — its two bridges are no-ops (`gamePlayStoreWrites`).
   */
  useEffect(() => {
    bridgeSetJoinedTournament(false);
    bridgeSetJoinedTournamentId(null);
  }, []);

  useEffect(() => {
    setNavigation(props?.navigation ?? null, props?.route?.name ?? null);
    setReconnect(reconnect ?? null);
    return () => {
      setNavigation(null, null);
      setReconnect(null);
    };
  }, [props?.navigation, props?.route?.name, reconnect]);

  return (
    <>
      {phone ? <PlayHeader /> : null}
      <PlayChessV2Body />
      <ClientChanged />
    </>
  );
}

export default PlayChessV2;
